/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { TFunction } from 'i18next';
import {
  buildDisclaimerSpans,
  DisclaimerUrls,
} from '../../utils/signupLoginUtils';
import { defaultProps, LoginPageProps } from './types';

export type { DisclaimerUrls };

/** Builds the translated disclaimer, tagging each segment with its URL. */
const buildDisclaimer = (t: TFunction, urls: DisclaimerUrls) => {
  // Typed as `string` explicitly: hosts that merge multiple namespaces into
  // `defaultNS` collapse this key's inferred type to `never`.
  const loginDisclaimerText: string = t('loginPage.disclaimer');
  const signUpDisclaimerText: string = t('signUpView.disclaimer');

  const isCompleteDisclaimer =
    loginDisclaimerText.includes('(i)') && loginDisclaimerText.includes('(ii)');

  // Prefer one complete sentence. Fragment concatenation re-appended T&C phrases
  // for locales whose login disclaimer already contained them (notably MY/ID/JA).
  const disclaimerText = isCompleteDisclaimer
    ? loginDisclaimerText
    : signUpDisclaimerText;

  return buildDisclaimerSpans(
    { ...(defaultProps.disclaimer || {}), text: disclaimerText },
    {
      'Terms and conditions': t('signUpView.termsAndConditions'),
      'Terms of Use': t('signUpView.termsOfUse'),
      'Privacy Statement': t('signUpView.privacyStatement'),
      'Terms of Service': t('signUpView.termsOfService'),
      'Privacy Policy': t('signUpView.privacyPolicy'),
    },
    urls,
  );
};

export const buildLoginViewProps = (
  props: LoginPageProps,
  t: TFunction,
  urls: DisclaimerUrls = {},
) => {
  const headings = {
    ...(defaultProps.headings ?? {}),
    ...props.headings,
    greeting: {
      ...(defaultProps.headings?.greeting ?? {}),
      text: t('loginPage.greeting'),
      ...props.headings?.greeting,
    },
    prompt: {
      ...(defaultProps.headings?.prompt ?? {}),
      text: t('loginPage.noAccount'),
      ...props.headings?.prompt,
    },
    action: {
      ...(defaultProps.headings?.action ?? {}),
      text: t('loginPage.signUp'),
      ...props.headings?.action,
    },
    forgotPassword: {
      ...(defaultProps.headings?.forgotPassword ?? {}),
      text: t('loginPage.forgotPassword'),
      ...props.headings?.forgotPassword,
    },
    dividerText: {
      ...(defaultProps.headings?.dividerText ?? {}),
      text: t('loginPage.dividerText'),
      ...props.headings?.dividerText,
    },
    continueAsGuestProps: {
      ...(defaultProps.headings?.continueAsGuestProps ?? {}),
      text: t('loginPage.continueAsGuest'),
      ...props.headings?.continueAsGuestProps,
    },
  };

  const emailFieldProps = {
    ...defaultProps.emailFieldProps,
    label: t('loginPage.emailLabel'),
    ...props.emailFieldProps,
  };

  const passwordFieldProps = {
    ...defaultProps.passwordFieldProps,
    label: t('loginPage.passwordLabel'),
    ...props.passwordFieldProps,
  };

  const mobileFieldProps = {
    ...defaultProps.mobileFieldProps,
    label: t('loginPage.mobileNumberLabel'),
    ...props.mobileFieldProps,
  };

  const dialingCodeFieldProps = {
    ...defaultProps.dialingCodeFieldProps,
    label: t('loginPage.dialCodeLabel'),
    ...props.dialingCodeFieldProps,
  };

  const primaryButtonProps = {
    ...defaultProps.primaryButtonProps,
    text: t('loginPage.loginButton'),
    ...props.primaryButtonProps,
  };

  const loginWithMobileNumber =
    props.loginWithMobileNumber ?? defaultProps.loginWithMobileNumber;

  const loginWithPasskey = {
    ...defaultProps.loginWithPasskey,
    text: t('loginPage.loginWithPasskey'),
    ...props.loginWithPasskey,
  };

  const socialLogins = {
    ...(defaultProps.socialLogins ?? {}),
    ...props.socialLogins,
    facebook: {
      ...(defaultProps.socialLogins?.facebook ?? {}),
      ...props.socialLogins?.facebook,
      label: {
        ...(defaultProps.socialLogins?.facebook?.label ?? {}),
        text: 'Facebook',
        ...props.socialLogins?.facebook?.label,
      },
    },
    google: {
      ...(defaultProps.socialLogins?.google ?? {}),
      ...props.socialLogins?.google,
      label: {
        ...(defaultProps.socialLogins?.google?.label ?? {}),
        text: 'Google',
        ...props.socialLogins?.google?.label,
      },
    },
    apple: {
      ...(defaultProps.socialLogins?.apple ?? {}),
      ...props.socialLogins?.apple,
      label: {
        ...(defaultProps.socialLogins?.apple?.label ?? {}),
        text: 'Apple',
        ...props.socialLogins?.apple?.label,
      },
    },
    wechat: {
      ...(defaultProps.socialLogins?.wechat ?? {}),
      ...props.socialLogins?.wechat,
      label: {
        ...(defaultProps.socialLogins?.wechat?.label ?? {}),
        text: 'WeChat',
        ...props.socialLogins?.wechat?.label,
      },
    },
  };

  const disclaimer = {
    ...buildDisclaimer(t, urls),
    ...props.disclaimer,
  };

  return {
    headings,
    emailFieldProps,
    passwordFieldProps,
    mobileFieldProps,
    dialingCodeFieldProps,
    primaryButtonProps,
    loginWithMobileNumber,
    loginWithPasskey,
    socialLogins,
    disclaimer,
  };
};

export type LoginViewResolvedProps = ReturnType<typeof buildLoginViewProps>;
